import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { CardPaymentMethodEditorViewModel } from '@/features/billing/payment-methods/CardPaymentMethodEditorViewModel'
import { type Member } from '@/features/iam/members/Member'
import { type Team } from '@/features/iam/teams/Team'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { observable, runInAction } from 'mobx'

/**
 * View model for the new payment method dialog.
 */
export class NewPaymentMethodDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<BillingMethod>({
    abort$: this.modal.close$,
  })

  /**
   * The editor of the current prompt.
   */
  @observable accessor editorVm: CardPaymentMethodEditorViewModel | null = null

  constructor(private readonly _billingProfileStore: BillingProfileStore) {}

  /**
   * Shows the editor and resolves with the created payment method, or `null`
   * when the dialog is closed first.
   */
  async prompt(member: Member, team: Team | null, signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.editorVm = new CardPaymentMethodEditorViewModel(
        member,
        team,
        this._billingProfileStore,
        (method) => this._prompt.respond(method),
      )
      this.modal.show()
    })

    const method = await responsePromise
    this.modal.close()
    return method
  }
}
